import produce from 'immer'
import { find, get, head, includes, isEmpty, isEqual, isUndefined, remove, set } from 'lodash'

import { ONE_TIME_TRIGGER } from 'components/SegmentSelector/outreach-message-trigger-constants'

import { SegmentName as ACADEMIC_FOCUS_KEY } from './AcademicFocusSelector'
import { SegmentName as CAREER_KEY } from './CareerSelector'
import { SegmentName as MEDIAN_INCOME_KEY } from './DemographicsSelector/MedianIncomeSelector'
import { SegmentName as NEIGHBORHOOD_INCOME_KEY } from './DemographicsSelector/NeighborhoodIncomeSelector'
import { SegmentName as HIGH_SCHOOL_KEY } from './HighSchool'
import { HAS_REDUCED_LUNCH_SEGMENT, IS_PRIVATE_SEGMENT } from './HighSchool/HighSchoolTypeSelector'
import { SegmentName as INTERESTS_KEY } from './InterestsSegmentSelector'
import { SegmentName as LEGAL_SEX_KEY } from './LegalSexSelector/LegalSexSelector'
import { SegmentName as NEIGHBORHOOD_TYPE_KEY } from './LocationSelector/NeighborhoodTypeSelector'
import { SegmentName as STATE_KEY } from './LocationSelector/StateSelector'
import { HSSID_SEGMENT, UUID_SEGMENT } from './ManualStudentSelector'
import { SegmentName as USER_TYPE_KEY } from './UserTypeSelector'
import {
  DEFAULT_USER_TYPE,
  USER_TYPE,
  addDefaultUserTypeSegment,
  getUserType,
  isMissingUserType,
} from './UserTypeSelector'
import { SegmentName as GRADUATION_YEAR_KEY } from './components/ClassYearSelector'
import { ENGAGEMENT_STAGES, SegmentName as ENGAGEMENT_STAGE_KEY } from './components/EngagementStage'
import { CLASS_YEARS } from './components/classyears-service'

export const DEFAULT_SEGMENTATION_VERSION = 2

export const MY_MATCHES_SEGMENT = 'MyMatches'

const getOperations = () => ({
  [INTERESTS_KEY]: 'contains',
  [ACADEMIC_FOCUS_KEY]: 'contains',
  [GRADUATION_YEAR_KEY]: 'in',
  [ENGAGEMENT_STAGE_KEY]: 'in',
  [USER_TYPE_KEY]: 'in',
  [HSSID_SEGMENT]: 'in',
  [UUID_SEGMENT]: 'in',
  [STATE_KEY]: 'in',
  [HIGH_SCHOOL_KEY]: 'in',
  [HAS_REDUCED_LUNCH_SEGMENT]: 'in',
  [IS_PRIVATE_SEGMENT]: 'in',
  [NEIGHBORHOOD_TYPE_KEY]: 'in',
  [MEDIAN_INCOME_KEY]: 'between',
  [NEIGHBORHOOD_INCOME_KEY]: 'in',
  [LEGAL_SEX_KEY]: 'in',
  [MY_MATCHES_SEGMENT]: 'in',
  [CAREER_KEY]: 'contains',
})

const MUST_FIELD = 'Must'
const ONE_OF_FIELD = 'OneOf'
const CRITERIA_FIELD = 'Criteria'

const CRITERIA_FIELD_TYPES = [MUST_FIELD, ONE_OF_FIELD]

const EMPTY_CRITERIA_OBJECT = {
  [MUST_FIELD]: [],
  [ONE_OF_FIELD]: [],
}

const EMPTY_SEGMENTATION = {
  Version: DEFAULT_SEGMENTATION_VERSION,
  [CRITERIA_FIELD]: EMPTY_CRITERIA_OBJECT,
}

const getCriteriaList = (segment, criteria) => get(segment, `${CRITERIA_FIELD}.${criteria}`, [])

const getDefaultSelectorType = (Name) => ({
  Name,
  Operation: getOperations()[Name],
  Values: [],
})

export const getDefaultSegment = (trigger, userType) => {
  return {
    Version: DEFAULT_SEGMENTATION_VERSION,
    [CRITERIA_FIELD]: getDefaultCriteria(trigger, userType, false),
  }
}

export const getDefaultMyMatchesSegment = (trigger, userType) => ({
  Version: DEFAULT_SEGMENTATION_VERSION,
  [CRITERIA_FIELD]: getDefaultCriteria(trigger, userType, true),
})

export const getDefaultFollowersSegment = (trigger, userType) => ({
  Version: DEFAULT_SEGMENTATION_VERSION,
  [CRITERIA_FIELD]: getDefaultCriteria(trigger, userType, false),
})

export const getIsOneTimeMessage = (trigger) => trigger === ONE_TIME_TRIGGER

export const getDefaultCriteria = (trigger, userType = DEFAULT_USER_TYPE, withMyMatches = false) => {
  const isOneTimeMessage = getIsOneTimeMessage(trigger)

  // create default segment
  const segment = getUpdatedSegment(EMPTY_SEGMENTATION, MUST_FIELD, GRADUATION_YEAR_KEY, CLASS_YEARS)

  // add default engagement stage
  const engagementStageValues = isOneTimeMessage && !withMyMatches ? ENGAGEMENT_STAGES : []
  let updated = getUpdatedSegment(segment, MUST_FIELD, ENGAGEMENT_STAGE_KEY, engagementStageValues)

  updated = getUpdatedSegment(updated, MUST_FIELD, USER_TYPE_KEY, [userType])

  if (withMyMatches) {
    updated = getUpdatedSegment(updated, MUST_FIELD, MY_MATCHES_SEGMENT, [true])
  }

  return updated.Criteria
}

const DO_NOT_SANITIZE = [HSSID_SEGMENT, UUID_SEGMENT]
export const getUpdatedSegment = (segment = {}, criteria, Name, Values, Operation) => {
  const updated = produce(segment, (draft) => {
    const criteriaList = getCriteriaList(draft, criteria)
    const foundSegment = remove(criteriaList, { Name })
    const segment = !isEmpty(foundSegment) ? head(foundSegment) : getDefaultSelectorType(Name)
    segment.Values = Values
    if (Operation) {
      segment.Operation = Operation
    }
    criteriaList.push(segment)
    set(draft, `Criteria.${criteria}`, criteriaList)
    return draft
  })
  if (includes(DO_NOT_SANITIZE, Name)) {
    return updated
  }
  return sanitizeSegmentOutput(updated)
}

export const setAcademicFocus = (segment, academicFocuses) =>
  getUpdatedSegment(segment, ONE_OF_FIELD, ACADEMIC_FOCUS_KEY, academicFocuses, getOperations()[ACADEMIC_FOCUS_KEY])

export const clearAcademicFocus = (segment) => {
  return getUpdatedSegment(segment, ONE_OF_FIELD, ACADEMIC_FOCUS_KEY, undefined)
}

export const setEngagementStage = (segment, stages) =>
  getUpdatedSegment(segment, MUST_FIELD, ENGAGEMENT_STAGE_KEY, stages, getOperations()[ENGAGEMENT_STAGE_KEY])

export const setInterest = (segment, interests) =>
  getUpdatedSegment(segment, ONE_OF_FIELD, INTERESTS_KEY, interests, getOperations()[INTERESTS_KEY])

export const clearInterests = (segment) => {
  return getUpdatedSegment(segment, ONE_OF_FIELD, INTERESTS_KEY, undefined)
}

export const setHighSchools = (segment, highschools) =>
  getUpdatedSegment(segment, MUST_FIELD, HIGH_SCHOOL_KEY, highschools, getOperations()[HIGH_SCHOOL_KEY])

export const getSegmentValue = (segment = {}, criteria, Name, defaultValue = []) => {
  const criteriaList = getCriteriaList(segment, criteria)
  return get(find(criteriaList, { Name }), 'Values', defaultValue)
}
export const getSegmentOperation = (segment = {}, criteria, Name, defaultValue = []) => {
  const criteriaList = getCriteriaList(segment, criteria)
  return get(find(criteriaList, { Name }), 'Operation', defaultValue)
}

export const hasSegment = (segment = {}, criteria, Name) => {
  const criteriaList = getCriteriaList(segment, criteria)
  return !isUndefined(get(find(criteriaList, { Name }), 'Values'))
}

const compareFields = (a, b, field) => {
  const aField = get(a, field, [])
  const bField = get(b, field, [])
  return isEqual(aField, bField)
}

export const checkIfSegmentIsDefault = (segment = {}, trigger) => {
  if (isEmpty(segment)) {
    return false
  }
  const userType = getUserType(segment)
  const givenCriteria = get(segment, CRITERIA_FIELD, EMPTY_CRITERIA_OBJECT)
  const targetCriteria = getDefaultSegment(trigger, userType).Criteria
  const mustMatches = compareFields(givenCriteria, targetCriteria, MUST_FIELD)
  const oneOfMatches = compareFields(givenCriteria, targetCriteria, ONE_OF_FIELD)
  return mustMatches && oneOfMatches
}

export const sanitizeSegmentOutput = (segment) => {
  const updated = produce(segment, (draft) => {
    if (isMissingUserType(draft)) {
      addDefaultUserTypeSegment(draft)
    }
    CRITERIA_FIELD_TYPES.forEach((criteria) => {
      const criteriaList = getCriteriaList(draft, criteria)
      remove(criteriaList, (criterium) => isEmpty(criterium.Values))
    })
  })
  return updated
}

// includes empty HSSID and UUID
export const getDefaultStudentIdSegment = () => {
  const segment = getUpdatedSegment(
    getDefaultFollowersSegment(ONE_TIME_TRIGGER, USER_TYPE.students),
    MUST_FIELD,
    HSSID_SEGMENT,
    [],
  )
  return getUpdatedSegment(segment, MUST_FIELD, UUID_SEGMENT, [])
}

export const getManualStudentSelection = (segment) => {
  const currentUUIDValue = getSegmentValue(segment, MUST_FIELD, UUID_SEGMENT)
  const currentHSSIDValue = getSegmentValue(segment, MUST_FIELD, HSSID_SEGMENT)
  if (currentUUIDValue && currentUUIDValue.length > 0) {
    return currentUUIDValue
  }
  return currentHSSIDValue
}

export const getIsUsingManualStudentSelection = (segment) => {
  return hasSegment(segment, MUST_FIELD, HSSID_SEGMENT) || hasSegment(segment, MUST_FIELD, UUID_SEGMENT)
}

export const getIsUsingMyMatches = (segment) => {
  return hasSegment(segment, MUST_FIELD, MY_MATCHES_SEGMENT)
}
